import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';
import PatientLayout from '@/components/PatientLayout';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PatientCategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [slug]);
  useEffect(() => {
    setFilteredArticles(articles.filter(a =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.excerpt && a.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
    ));
  }, [searchTerm, articles]);

  const fetchData = async () => {
    try {
      const catResponse = await fetch(`${API}/api/patient-categories`);
      if (catResponse.ok) {
        const allCats = await catResponse.json();
        setCategories(allCats);
        const cat = allCats.find(c => c.slug === slug);
        setCategory(cat);
        if (cat) {
          const artResponse = await fetch(`${API}/api/patient-articles?category_id=${cat.id}&status=published`);
          if (artResponse.ok) {
            const data = await artResponse.json();
            setArticles(data);
            setFilteredArticles(data);
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1e3a5f]"></div>
        </div>
      </PatientLayout>
    );
  }

  if (!category) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center py-32 px-4">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Catégorie non trouvée</h2>
            <Link to="/patients" className="text-[#35AFE4] hover:underline text-sm">Retour à l'accueil</Link>
          </div>
        </div>
      </PatientLayout>
    );
  }

  const getCategoryLink = (cat) => cat.slug === 'faq' ? '/patients/faq' : `/patients/categorie/${cat.slug}`;

  return (
    <PatientLayout>
      <SEO
        title={`${category.name} - Espace Patients`}
        description={category.description || `${category.name} - Conseils et informations pour les porteurs de lentilles de contact`}
        canonical={`https://sfo-alc.fr/patients/categorie/${category.slug}`}
      />
      {/* CTA Banner - matching reference */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6187 50%, #35AFE4 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center py-8 sm:py-12">
          <h2 className="text-white text-lg sm:text-2xl font-bold mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>
            L'annuaire des praticiens
          </h2>
          <p className="text-white/80 text-xs sm:text-sm mb-3 sm:mb-4">
            Trouvez facilement un Ophtalmologiste Adaptateur de lentilles de contact
          </p>
          <Link
            to="/annuaire"
            className="inline-block px-5 sm:px-7 py-2 sm:py-2.5 bg-white text-[#1e3a5f] rounded-lg text-xs sm:text-sm font-bold hover:bg-gray-100 transition-colors shadow"
          >
            Accéder à l'annuaire en ligne
          </Link>
        </div>
      </div>

      {/* Content with Sidebar */}
      <div className="bg-gray-50 py-6 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
            {/* Sidebar - matching reference (Les outils) */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 sticky top-28" data-testid="category-sidebar">
                <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  Les outils
                </h3>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed hidden sm:block">
                  Nous mettons à votre disposition divers outils et informations pour vous accompagner au quotidien
                </p>
                <nav className="space-y-1">
                  {categories.map(cat => (
                    <Link
                      key={cat.id}
                      to={getCategoryLink(cat)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        cat.slug === slug
                          ? 'bg-[#35AFE4]/10 text-[#1e3a5f] font-semibold'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-[#1e3a5f]'
                      }`}
                    >
                      <span className="truncate">{cat.name}</span>
                      <ChevronRight size={14} className="flex-shrink-0 opacity-40" />
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Breadcrumb */}
              <div className="mb-4">
                <Link to="/patients" className="inline-flex items-center gap-1.5 text-[#35AFE4] hover:text-[#1e3a5f] text-xs sm:text-sm transition-colors" data-testid="back-to-patients">
                  <ArrowLeft size={14} />
                  <span>Espace patients</span>
                </Link>
              </div>

              {/* Category Header */}
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="category-title">
                {category.name}
              </h1>
              <p className="text-gray-500 text-sm mb-6">{category.description}</p>

              {/* Search */}
              <div className="mb-6 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un article..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] text-sm bg-white"
                    data-testid="category-search-input"
                  />
                </div>
              </div>

              {/* Articles */}
              {filteredArticles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'Aucun article trouvé' : 'Aucun article disponible'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5" data-testid="articles-grid">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/patients/article/${article.slug}`}
                      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100"
                      data-testid={`article-card-${article.slug}`}
                    >
                      {/* Thumbnail */}
                      <div className="h-32 sm:h-40 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
                        {article.featured_image ? (
                          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <img src="/community-icon.svg" alt="" className="w-12 h-12 opacity-40" />
                          </div>
                        )}
                      </div>
                      <div className="p-4 sm:p-5">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1.5 group-hover:text-[#35AFE4] transition-colors line-clamp-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                          {article.title}
                        </h3>
                        {article.excerpt && (
                          <p className="text-gray-500 text-xs sm:text-sm line-clamp-2 leading-relaxed">{article.excerpt}</p>
                        )}
                        <span className="inline-block mt-2.5 text-[#35AFE4] font-medium text-xs sm:text-sm group-hover:translate-x-1 transition-transform">
                          Découvrir &rarr;
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PatientLayout>
  );
};

export default PatientCategoryPage;
